import * as React from "react";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

/**
 * Stub of `react-data-table-component`'s `defaultThemes` registry. Only the
 * shape that `src/styles.ts` reads (`default.text.primary`,
 * `default.divider.default`) is provided. The CSS-in-JS `customStyles` it
 * feeds into is silently dropped by this shim, so the values are essentially
 * documentation — kept here so existing imports keep compiling.
 */
export const defaultThemes: Record<string, any> = {
  default: {
    text: { primary: "rgba(0, 0, 0, 0.87)", secondary: "rgba(0, 0, 0, 0.54)", disabled: "rgba(0, 0, 0, 0.38)" },
    divider: { default: "rgba(0, 0, 0, 0.12)" },
    background: { default: "#FFFFFF" },
    context: { background: "#e3f2fd", text: "rgba(0, 0, 0, 0.87)" },
    button: { default: "rgba(0,0,0,.54)", focus: "rgba(0,0,0,.12)", hover: "rgba(0,0,0,.12)", disabled: "rgba(0, 0, 0, .18)" },
    selected: { default: "#e3f2fd", text: "rgba(0, 0, 0, 0.87)" },
    highlightOnHover: { default: "#EEEEEE", text: "rgba(0, 0, 0, 0.87)" },
    striped: { default: "#FAFAFA", text: "rgba(0, 0, 0, 0.87)" },
  },
};

/**
 * Drop-in shim for `react-data-table-component`'s default `<DataTable>`.
 * Renders shadcn `<Table>` primitives via `@tanstack/react-table`.
 *
 * Translates the `react-data-table-component` column shape on the fly:
 *   - `name`       → header text
 *   - `selector`   → accessor (used for sorting + default cell display)
 *   - `cell`       → custom cell renderer
 *   - `sortable`   → enableSorting
 *   - `width`      → column size (CSS string respected)
 *   - `grow`       → flex-grow proportion (`flex` style on `<th>` / `<td>`)
 *
 * Fluent-style `customStyles` is silently dropped (same trade-off the rest of
 * the migration uses). Long-tail features (`subHeader`, `sortIcon`) aren't
 * implemented — neither is used in this codebase today.
 *
 * `selectableRows`, `expandableRows` and `conditionalRowStyles` ARE implemented, all opt-in:
 * without the prop the table renders exactly as before — no extra column, no extra row,
 * unchanged colSpans, no style merge.
 */

// ─── react-data-table-component column shape ──────────────────────────────────

export interface RDTColumn<T = any> {
  name?: React.ReactNode;
  selector?: (row: T) => unknown;
  cell?: (row: T, rowIndex?: number) => React.ReactNode;
  sortable?: boolean;
  /** Pixel string ('300px') or any CSS width. */
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  /** Flex-grow factor (only effective when no fixed `width`). */
  grow?: number;
  right?: boolean;
  center?: boolean;
  /**
   * Let the cell break lines. **Set this on every column holding free text.**
   *
   * Without it a cell is `nowrap` + `overflow:hidden` + `text-overflow:ellipsis`, which reads like
   * truncation but is not: the table is auto-layout, so instead of clipping, it *widens* to fit the
   * longest content in that column. The columns behind it then slide past the container edge —
   * reachable only through a scrollbar macOS does not draw, so they simply look missing. Measured on
   * the tender board: one long TED title took the table to 2624 px inside a 1728 px window; `wrap`
   * brought it back to 1345 px with every column at its configured width.
   */
  wrap?: boolean;
  hide?: any;
  id?: string;
  /** Drops */
  ignoreRowClick?: boolean;
  button?: boolean;
  allowOverflow?: boolean;
  style?: React.CSSProperties;
  conditionalCellStyles?: any[];
  format?: (row: T) => React.ReactNode;
  sortFunction?: (rowA: T, rowB: T) => number;
}

export interface PaginationComponentOptions {
  rowsPerPageText?: string;
  rangeSeparatorText?: string;
  selectAllRowsItem?: boolean;
  selectAllRowsItemText?: string;
}

/**
 * Props handed to a custom `rowComponent`. Everything except `row` is what the
 * table would otherwise put on its own `<TableRow>` — spread it onto the
 * `<TableRow>` you render so row styling and row events keep working.
 */
export interface RowComponentProps<T = any> {
  row: T;
  children: React.ReactNode;
  className?: string;
  "data-state"?: "selected";
  onClick?: React.MouseEventHandler<HTMLTableRowElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLTableRowElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLTableRowElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLTableRowElement>;
}

export interface FluentDataTableProps<T = any> {
  data: T[];
  columns: RDTColumn<T>[];
  /** Field to use as a stable row id (e.g. `'_id'`). Defaults to `'id'`. */
  keyField?: string;
  /** Render each row's `<TableRow>` yourself — needed when a row has to carry a
   *  ref (e.g. a react-dnd drop target). Omit it and the table renders rows
   *  itself, unchanged. */
  rowComponent?: React.ComponentType<RowComponentProps<T>>;

  // Selectable rows
  selectableRows?: boolean;
  selectableRowsSingle?: boolean;
  selectableRowsHighlight?: boolean;
  selectableRowDisabled?: (row: T) => boolean;
  /** Toggle this prop (flip the boolean / change the value) to clear the
   *  table's internal selection — same contract as
   *  `react-data-table-component`. */
  clearSelectedRows?: boolean | any;
  onSelectedRowsChange?: (state: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: T[];
  }) => void;
  selectableRowsComponent?: any;
  selectableRowsComponentProps?: any;

  // Layout / behaviour
  pagination?: boolean;
  paginationPerPage?: number;
  paginationRowsPerPageOptions?: number[];
  paginationDefaultPage?: number;
  paginationComponentOptions?: PaginationComponentOptions;
  paginationServer?: boolean; // server-side pagination: `data` is one page, `paginationTotalRows` the total
  paginationTotalRows?: number;
  /** Controlled current page (1-based) — keeps the control in sync with the caller,
      in server *and* client pagination. */
  page?: number;
  onChangePage?: (page: number, totalRows?: number) => void;
  onChangeRowsPerPage?: (rowsPerPage: number, page: number) => void;

  // States
  noDataComponent?: React.ReactNode;
  progressPending?: boolean;
  progressComponent?: React.ReactNode;

  // Expandable rows
  //
  // Mirrors `react-data-table-component`'s contract: an expander column is prepended, and an
  // expanded row renders `expandableRowsComponent` in a full-width row directly beneath its own.
  // Works controlled (pass `expandableRowExpanded`) or uncontrolled (internal state).
  /** Enable the feature. Without it nothing changes — no extra column, no extra row. */
  expandableRows?: boolean;
  /** Rendered inside the expanded row. Receives the row's data as `data`, like RDT. */
  expandableRowsComponent?: React.ComponentType<{ data: T }>;
  /** Controlled expansion. When provided, the component never keeps its own state. */
  expandableRowExpanded?: (row: T) => boolean;
  /** Fired on expander click with the *next* expansion state. */
  onRowExpandToggled?: (expanded: boolean, row: T) => void;
  /** Rows that cannot be expanded — the expander cell stays empty. */
  expandableRowDisabled?: (row: T) => boolean;

  // Row events
  onRowClicked?: (row: T, ev?: React.MouseEvent) => void;
  onRowDoubleClicked?: (row: T, ev?: React.MouseEvent) => void;
  onRowMouseEnter?: (row: T, ev?: React.MouseEvent) => void;
  onRowMouseLeave?: (row: T, ev?: React.MouseEvent) => void;

  // Visual flags
  striped?: boolean;
  dense?: boolean;
  /** Use CSS `table-layout: fixed` so column widths honour width/min/maxWidth and
   *  content truncates (ellipsis) instead of the column growing to fit / jumping. */
  fixedLayout?: boolean;
  highlightOnHover?: boolean;
  pointerOnHover?: boolean;
  responsive?: boolean;
  fixedHeader?: boolean;
  fixedHeaderScrollHeight?: string;

  // Drops
  customStyles?: any;
  theme?: any;
  /**
   * Per-row styling — `react-data-table-component`'s contract: the first entry
   * whose `when` matches contributes its `style`, later matches merge on top.
   */
  conditionalRowStyles?: { when: (row: T) => boolean; style?: React.CSSProperties }[];
  subHeader?: any;
  subHeaderComponent?: any;
  subHeaderAlign?: any;
  subHeaderWrap?: any;
  sortIcon?: React.ReactNode;
  defaultSortFieldId?: any;
  defaultSortAsc?: boolean;
  paginationIconFirstPage?: any;
  paginationIconLastPage?: any;
  paginationIconNext?: any;
  paginationIconPrevious?: any;
  className?: string;
  style?: React.CSSProperties;
  noHeader?: boolean;
}

// Build a tanstack ColumnDef list from the react-data-table-component shape.
function toTanstackColumns<T>(cols: RDTColumn<T>[]): ColumnDef<T, any>[] {
  return cols.map((c, idx): ColumnDef<T, any> => {
    const id = c.id ?? `col-${idx}`;
    // Header text comes from `name`. Sorting reads from `selector`.
    return {
      id,
      header: typeof c.name === "string" ? c.name : () => <>{c.name}</>,
      accessorFn: c.selector ? (row: T) => c.selector!(row) : undefined,
      enableSorting: !!c.sortable,
      // Only set `sortingFn` when a custom comparator is given. Setting it to an
      // explicit `undefined` would clobber TanStack's default (the resolved
      // columnDef is `{ ...defaultColumnDef, ...columnDef }`), leaving no sort
      // function and crashing with "sortingFn is not a function" — same gotcha
      // as the `cell` note below.
      ...(c.sortFunction
        ? {
            sortingFn: (rowA: any, rowB: any) =>
              c.sortFunction!(rowA.original as T, rowB.original as T),
          }
        : {}),
      // NOTE: never leave `cell` as an explicit `undefined` — the resolved
      // columnDef is `{ ...defaultColumnDef, ...columnDef }`, so an explicit
      // undefined clobbers TanStack's default renderer and selector-only
      // columns come out empty. Fall back to rendering the accessor value,
      // matching react-data-table-component's selector semantics.
      cell: c.cell
        ? ({ row, column }: any) => c.cell!(row.original as T, column.getIndex())
        : c.format
          ? ({ row }: any) => c.format!(row.original as T)
          : ({ getValue }: any) => {
              const value = getValue();
              return value === null || value === undefined ? null : String(value);
            },
      // Tanstack stores numeric `size` for column widths; we keep the CSS
      // width string in `meta` and apply it via inline style on <th>/<td>.
      meta: {
        cssWidth: c.width,
        cssMinWidth: c.minWidth,
        cssMaxWidth: c.maxWidth,
        flexGrow: c.grow,
        align: c.right ? "right" : c.center ? "center" : "left",
        wrap: c.wrap,
      },
    };
  });
}

interface ColumnMeta {
  cssWidth?: string;
  cssMinWidth?: string;
  cssMaxWidth?: string;
  flexGrow?: number;
  align?: "left" | "right" | "center";
  wrap?: boolean;
}

const cellStyleFromMeta = (meta?: ColumnMeta): React.CSSProperties => {
  if (!meta) return {};
  const out: React.CSSProperties = {};
  if (meta.cssWidth) out.width = meta.cssWidth;
  if (meta.cssMinWidth) out.minWidth = meta.cssMinWidth;
  if (meta.cssMaxWidth) out.maxWidth = meta.cssMaxWidth;
  if (meta.flexGrow != null && !meta.cssWidth) out.flex = `${meta.flexGrow} 1 0`;
  if (meta.align && meta.align !== "left") out.textAlign = meta.align;
  if (!meta.wrap) {
    out.whiteSpace = "nowrap";
    out.overflow = "hidden";
    out.textOverflow = "ellipsis";
  }
  return out;
};

export function DataTable<T>({
  data,
  columns,
  keyField = "id",
  expandableRows,
  expandableRowsComponent: ExpandableRowsComponent,
  expandableRowExpanded,
  onRowExpandToggled,
  expandableRowDisabled,
  selectableRows,
  selectableRowsSingle,
  selectableRowDisabled,
  clearSelectedRows,
  conditionalRowStyles,
  onSelectedRowsChange,
  pagination,
  paginationPerPage = 10,
  paginationRowsPerPageOptions: _paginationRowsPerPageOptions = [10, 25, 50, 100],
  paginationDefaultPage = 1,
  paginationComponentOptions,
  paginationServer,
  paginationTotalRows,
  page: controlledPage,
  onChangePage,
  onChangeRowsPerPage,
  noDataComponent,
  progressPending,
  progressComponent,
  onRowClicked,
  onRowDoubleClicked,
  onRowMouseEnter,
  onRowMouseLeave,
  striped,
  dense,
  fixedLayout,
  highlightOnHover = true,
  pointerOnHover,
  fixedHeader,
  fixedHeaderScrollHeight,
  className,
  style,
  noHeader,
  rowComponent: RowComponent,
}: FluentDataTableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination_, setPagination] = React.useState({
    pageIndex: Math.max(0, paginationDefaultPage - 1),
    pageSize: paginationPerPage,
  });

  // Row-id helper. `keyField` is the row property to use as the stable id —
  // matches `react-data-table-component`'s contract. Falls back to the row's
  // index when the field isn't present.
  const getRowKey = React.useCallback(
    (row: any, idx: number): string => {
      const v = row?.[keyField];
      return v != null ? String(v) : String(idx);
    },
    [keyField],
  );

  // Expansion state — only consulted when the caller does NOT control expansion.
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());
  const isControlledExpansion = typeof expandableRowExpanded === "function";

  const isRowExpanded = React.useCallback(
    (rowData: any, rowKey: string): boolean =>
      isControlledExpansion ? !!expandableRowExpanded!(rowData) : expandedKeys.has(rowKey),
    [isControlledExpansion, expandableRowExpanded, expandedKeys],
  );

  const toggleRowExpansion = React.useCallback(
    (rowData: any, rowKey: string, currentlyExpanded: boolean) => {
      // The caller is told the *next* state either way; only the storage differs.
      if (!isControlledExpansion) {
        setExpandedKeys((prev) => {
          const next = new Set(prev);
          if (currentlyExpanded) next.delete(rowKey);
          else next.add(rowKey);
          return next;
        });
      }
      onRowExpandToggled?.(!currentlyExpanded, rowData);
    },
    [isControlledExpansion, onRowExpandToggled],
  );

  // Selection state — keyed by the row id from `keyField`.
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(new Set());


  // `react-data-table-component`'s contract: any change to `clearSelectedRows`
  // (typically flipping a boolean) wipes the table's internal selection. We
  // skip the very first run so we don't clobber a controlled initial state.
  const clearSelectedRowsRef = React.useRef(clearSelectedRows);
  React.useEffect(() => {
    if (clearSelectedRowsRef.current === clearSelectedRows) return;
    clearSelectedRowsRef.current = clearSelectedRows;
    setSelectedKeys(new Set());
  }, [clearSelectedRows]);

  // Fire onSelectedRowsChange whenever the selection set changes.
  React.useEffect(() => {
    if (!selectableRows || !onSelectedRowsChange) return;
    const list = (data ?? []).filter((row, i) => selectedKeys.has(getRowKey(row, i)));
    onSelectedRowsChange({
      allSelected: list.length > 0 && list.length === (data ?? []).length,
      selectedCount: list.length,
      selectedRows: list,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, data]);

  const toggleRowSelection = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (selectableRowsSingle) {
        // Single mode: replace whatever was selected.
        next.clear();
        if (!prev.has(key)) next.add(key);
      } else {
        if (next.has(key)) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };

  const toggleAllSelection = (visibleKeys: string[]) => {
    setSelectedKeys((prev) => {
      const allSelected = visibleKeys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach((k) => next.delete(k));
      else visibleKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  // Update pageSize if the prop changes mid-flight.
  React.useEffect(() => {
    setPagination((p) => ({ ...p, pageSize: paginationPerPage }));
  }, [paginationPerPage]);

  const tableColumns = React.useMemo(() => toTanstackColumns(columns), [columns]);

  const table = useReactTable<T>({
    data: data ?? [],
    columns: tableColumns,
    getRowId: (row: any, idx: number) => getRowKey(row, idx),
    state: {
      sorting,
      ...(pagination ? { pagination: pagination_ } : {}),
    },
    onSortingChange: setSorting,
    onPaginationChange: pagination
      ? (updater) => {
          setPagination((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            if (next.pageIndex !== prev.pageIndex) {
              onChangePage?.(next.pageIndex + 1, data?.length);
            }
            if (next.pageSize !== prev.pageSize) {
              onChangeRowsPerPage?.(next.pageSize, next.pageIndex + 1);
            }
            return next;
          });
        }
      : undefined,
    // react-data-table-component keeps the current page when `data` changes;
    // TanStack otherwise resets to page 1 on every data update (default
    // autoResetPageIndex: true). That reset clobbered a page restored from the
    // URL (e.g. ?productListPage=77) the moment async data arrived — and even
    // rewrote the URL back to page 1 via onChangePage. Disable it to match RDT.
    autoResetPageIndex: false,
    // Server-side pagination: `data` is already the current page, so don't slice
    // it; the total row count drives the page count instead.
    ...(paginationServer
      ? { manualPagination: true, rowCount: paginationTotalRows ?? 0 }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    ...(paginationServer ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(pagination && !paginationServer ? { getPaginationRowModel: getPaginationRowModel() } : {}),
  });

  // Keep the control's page in sync with the caller.
  //
  // Originally server-mode only, where the caller owns the page anyway. It
  // applies to client-side pagination too: "select this row" is a normal thing
  // for a caller to want — a click on the floorplan selecting the room in the
  // table — and the row is useless if it sits on a page nobody is looking at.
  React.useEffect(() => {
    if (typeof controlledPage === "number") {
      setPagination((prev) =>
        prev.pageIndex === controlledPage - 1 ? prev : { ...prev, pageIndex: controlledPage - 1 },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledPage, paginationServer]);

  // Companion to autoResetPageIndex: false — when the data set shrinks (e.g.
  // a filter change drops 606 rows to 2) the preserved page index can point
  // past the last page, rendering an empty table. Clamp to the last valid
  // page; in-range indices (the URL-restore case) stay untouched.
  // ZERO rows are deliberately exempt: async reloads pass `data=[]` while
  // loading, and clamping then would reset the page on every reload —
  // breaking pagination wherever a page change triggers a refetch.
  React.useEffect(() => {
    if (!pagination || paginationServer) return;
    const rowCount = data?.length ?? 0;
    if (rowCount === 0) return;
    const pageCount = Math.max(1, Math.ceil(rowCount / pagination_.pageSize));
    if (pagination_.pageIndex > pageCount - 1) {
      setPagination((prev) => ({ ...prev, pageIndex: pageCount - 1 }));
      onChangePage?.(pageCount, rowCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length, pagination_.pageIndex, pagination_.pageSize, pagination]);

  const headers = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const totalRows = paginationServer ? paginationTotalRows ?? 0 : data?.length ?? 0;
  const pageIndex = table.getState().pagination?.pageIndex ?? 0;
  const pageSize = table.getState().pagination?.pageSize ?? paginationPerPage;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  // ── Loading / empty states (no headers, no rows). ─────────────────────────
  const showLoading = !!progressPending;
  const showEmpty = !showLoading && rows.length === 0;

  return (
    <div
      className={cn("w-full text-sm", className)}
      style={style}
    >
      <div
        className={cn(fixedHeader && "overflow-y-auto")}
        style={fixedHeader ? { maxHeight: fixedHeaderScrollHeight } : undefined}
      >
        <Table className={cn(fixedLayout && "table-fixed w-full", striped && "[&_tbody_tr:nth-child(odd)]:bg-muted/30")}>
          {!noHeader && (
            <TableHeader className={cn(fixedHeader && "sticky top-0 z-10 bg-background")}>
              {headers.map((group) => (
                <TableRow key={group.id}>
                  {expandableRows && (
                    // Empty header over the expander column — without it every heading is off by
                    // one against its data.
                    <TableHead className={cn(dense ? "h-8 py-1" : "h-10", "w-9")} />
                  )}
                  {selectableRows && (
                    <TableHead className={cn(dense ? "h-8 py-1" : "h-10", "w-10")}>
                      {!selectableRowsSingle && (() => {
                        const visibleKeys = rows.map((r) => r.id);
                        const allChecked =
                          visibleKeys.length > 0 &&
                          visibleKeys.every((k) => selectedKeys.has(k));
                        const someChecked =
                          !allChecked &&
                          visibleKeys.some((k) => selectedKeys.has(k));
                        return (
                          <Checkbox
                            checked={allChecked ? true : someChecked ? "indeterminate" : false}
                            onCheckedChange={() => toggleAllSelection(visibleKeys)}
                            aria-label={t("settingsPanel.dataTable.selectAllRowsAriaLabel")}
                          />
                        );
                      })()}
                    </TableHead>
                  )}
                  {group.headers.map((h) => {
                    const meta = h.column.columnDef.meta as ColumnMeta | undefined;
                    const sortDir = h.column.getIsSorted();
                    const canSort = h.column.getCanSort();
                    return (
                      <TableHead
                        key={h.id}
                        style={cellStyleFromMeta(meta)}
                        className={cn(
                          dense ? "h-8 py-1" : "h-10",
                          canSort && "cursor-pointer select-none",
                        )}
                        onClick={
                          canSort
                            ? h.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                          {canSort && (
                            sortDir === "asc" ? (
                              <ChevronUp size={14} className="opacity-70" />
                            ) : sortDir === "desc" ? (
                              <ChevronDown size={14} className="opacity-70" />
                            ) : (
                              <ChevronsUpDown size={12} className="opacity-30" />
                            )
                          )}
                        </span>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            {showLoading ? (
              <TableRow>
                <TableCell
                  colSpan={(tableColumns.length || 1) + (selectableRows ? 1 : 0) + (expandableRows ? 1 : 0)}
                  className="h-24 text-center"
                >
                  {progressComponent ?? (
                    <span className="text-muted-foreground">
                      {t("settingsPanel.dataTable.loadingText")}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ) : showEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={(tableColumns.length || 1) + (selectableRows ? 1 : 0) + (expandableRows ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {noDataComponent ?? t("settingsPanel.dataTable.noRecordsText")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const rowKey = row.id;
                const isSelected = selectedKeys.has(rowKey);
                const isDisabled = selectableRowDisabled?.(row.original);
                const rowProps = {
                  "data-state": isSelected ? ("selected" as const) : undefined,
                  className: cn(
                    dense && "[&>td]:py-1",
                    highlightOnHover && "hover:bg-muted/40",
                    pointerOnHover && "cursor-pointer",
                    isSelected && "bg-muted/50",
                  ),
                  onClick: onRowClicked
                    ? (e: React.MouseEvent<HTMLTableRowElement>) =>
                        onRowClicked(row.original, e)
                    : undefined,
                  onDoubleClick: onRowDoubleClicked
                    ? (e: React.MouseEvent<HTMLTableRowElement>) =>
                        onRowDoubleClicked(row.original, e)
                    : undefined,
                  onMouseEnter: onRowMouseEnter
                    ? (e: React.MouseEvent<HTMLTableRowElement>) =>
                        onRowMouseEnter(row.original, e)
                    : undefined,
                  onMouseLeave: onRowMouseLeave
                    ? (e: React.MouseEvent<HTMLTableRowElement>) =>
                        onRowMouseLeave(row.original, e)
                    : undefined,
                  // Declared but never applied until now, which made a caller's
                  // highlight silently do nothing — the same trap as `styles` on
                  // the other compat shims.
                  style: (conditionalRowStyles ?? []).reduce(
                    (merged: React.CSSProperties, rule: any) =>
                      rule?.when?.(row.original) ? { ...merged, ...(rule.style ?? {}) } : merged,
                    {} as React.CSSProperties,
                  ),
                };
                const Row: any = RowComponent ?? TableRow;
                const expanded = expandableRows && isRowExpanded(row.original, rowKey);
                const expandDisabled = expandableRowDisabled?.(row.original);
                return (
                <React.Fragment key={row.id}>
                <Row
                  {...rowProps}
                  {...(RowComponent ? { row: row.original } : {})}
                >
                  {expandableRows && (
                    <TableCell
                      className={cn(dense ? "py-1" : "py-2", "w-9 align-middle")}
                      // The expander must not also trigger onRowClicked — a row that both
                      // expands and navigates on the same click is unusable.
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!expandDisabled && (
                        <button
                          type="button"
                          aria-expanded={!!expanded}
                          aria-label={t("settingsPanel.dataTable.expandRow")}
                          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                          onClick={() => toggleRowExpansion(row.original, rowKey, !!expanded)}
                        >
                          <ChevronRight
                            size={14}
                            className={cn("transition-transform", expanded && "rotate-90")}
                          />
                        </button>
                      )}
                    </TableCell>
                  )}
                  {selectableRows && (
                    <TableCell
                      className={cn(dense ? "py-1" : "py-2", "w-10")}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => toggleRowSelection(rowKey)}
                        aria-label={`Select row`}
                      />
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        style={cellStyleFromMeta(meta)}
                        className={cn(dense ? "py-1" : "py-2")}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </Row>
                {expanded && ExpandableRowsComponent && (
                  <TableRow
                    // Not hoverable and not clickable: it is a detail panel, not a record.
                    className="bg-muted/30 hover:bg-muted/30"
                  >
                    <TableCell
                      colSpan={
                        row.getVisibleCells().length +
                        (expandableRows ? 1 : 0) +
                        (selectableRows ? 1 : 0)
                      }
                      className="p-0"
                    >
                      <ExpandableRowsComponent data={row.original} />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Pagination footer ──────────────────────────────────────────── */}
      {pagination && totalRows > 0 && !showLoading && (
        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <div>
            {(() => {
              const first = pageIndex * pageSize + 1;
              const last = Math.min(totalRows, (pageIndex + 1) * pageSize);
              // A per-call-site `rangeSeparatorText` still wins (callers using
              // `useDataTablePaginationOptions` pass one). The fallback reads
              // the *same* key that helper does, so the separator has a single
              // source of truth whether or not the caller overrides it.
              const sep =
                paginationComponentOptions?.rangeSeparatorText ??
                t("settingsPanel.dataTable.rangeSeparatorText");
              return `${first}–${last} ${sep} ${totalRows}`;
            })()}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              {t("settingsPanel.dataTable.previousText")}
            </Button>
            <span className="px-2">
              {pageIndex + 1} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              {t("settingsPanel.dataTable.nextText")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
